import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { lookupUserBySlug } from '../../util/api';

const { UUID } = sdkTypes;

// Helper to check if a string is a valid UUID format
const isUUID = str => {
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(str);
};

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Apply custom listing order based on user's liveListingOrder publicData
 * @param {Array} listingRefs - Array of listing refs { id, type }
 * @param {Array} liveListingOrder - Array of listing UUIDs in desired order
 * @returns {Array} Sorted listing refs
 */
const applyCustomListingOrder = (listingRefs, liveListingOrder) => {
  if (!liveListingOrder || liveListingOrder.length === 0) {
    return listingRefs;
  }

  // Create a map of listing ID to its position in the custom order
  const orderMap = new Map(liveListingOrder.map((id, index) => [id, index]));

  // Sort listing refs: ordered ones first (by their position), then unordered ones
  return [...listingRefs].sort((a, b) => {
    const orderA = orderMap.has(a.id.uuid) ? orderMap.get(a.id.uuid) : Infinity;
    const orderB = orderMap.has(b.id.uuid) ? orderMap.get(b.id.uuid) : Infinity;
    return orderA - orderB;
  });
};

export const queryUserListings = (userId, config, ownProfileOnly = false, profileUserData = null) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const queryParams = {
    include: ['author', 'images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  const listingsPromise = ownProfileOnly
    ? sdk.ownListings.query({
      states: ['published'],
      ...queryParams,
    })
    : sdk.listings.query({
      author_id: userId,
      ...queryParams,
    });

  return listingsPromise
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      let listingRefs = listings
        .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(({ id, type }) => ({ id, type }));

      // Apply custom listing order if available from profile user's publicData
      // For own profile, use current user's publicData
      // For other profiles, profileUserData should contain the user's publicData
      const state = getState();
      const currentUser = state.user?.currentUser;
      const isOwnProfile = currentUser?.id?.uuid === userId.uuid;

      let liveListingOrder = null;
      if (isOwnProfile && currentUser?.attributes?.profile?.publicData?.liveListingOrder) {
        liveListingOrder = currentUser.attributes.profile.publicData.liveListingOrder;
      } else if (profileUserData?.attributes?.profile?.publicData?.liveListingOrder) {
        liveListingOrder = profileUserData.attributes.profile.publicData.liveListingOrder;
      }

      if (liveListingOrder) {
        listingRefs = applyCustomListingOrder(listingRefs, liveListingOrder);
      }

      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const showUser = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

const isCurrentUser = (userId, cu) => userId?.uuid === cu?.id?.uuid;

// Internal function to load profile data once we have the UUID
const loadProfileData = (userId, params, config) => (dispatch, getState, sdk) => {
  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const currentUser = getState()?.user?.currentUser;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  if (isPreviewForCurrentUser) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        // Scenario: 'active' user somehow tries to open a link for "variant" profile
        // First fetch user data, then query listings with that data to get custom order
        return dispatch(showUser(userId, config)).then(userResponse => {
          const profileUser = userResponse?.data?.data;
          return Promise.all([
            dispatch(queryUserListings(userId, config, false, profileUser)),
            dispatch(queryUserReviews(userId)),
          ]);
        });
      } else if (isCurrentUser(userId, currentUser)) {
        // Handle a scenario, where user (in pending-approval state)
        // tries to see their own profile page.
        // => just set userId to state
        return dispatch(showUserRequest(userId));
      } else {
        return Promise.resolve({});
      }
    });
  }

  // Fetch data for plain profile page.
  // Note 1: returns 404s if user is not 'active'.
  // Note 2: In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  // On a private marketplace, show active (approved) current user's own page
  // even if they don't have viewing rights
  const canFetchOwnProfileOnly =
    isPrivateMarketplace &&
    isAuthorized &&
    hasNoViewingRights &&
    isCurrentUser(userId, currentUser);

  if (!canFetchData) {
    return Promise.resolve();
  } else if (canFetchOwnProfileOnly) {
    return Promise.all([
      dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
      dispatch(queryUserListings(userId, config, canFetchOwnProfileOnly)),
      dispatch(showUserRequest(userId)),
    ]);
  }

  // For public profile pages, fetch user data first to get their custom listing order
  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
    return dispatch(showUser(userId, config)).then(userResponse => {
      const profileUser = userResponse?.data?.data;
      return Promise.all([
        dispatch(queryUserListings(userId, config, false, profileUser)),
        dispatch(queryUserReviews(userId)),
      ]);
    });
  });
};

// Helper to perform slug lookup that works in both SSR and client environments
const performSlugLookup = async identifier => {
  const isSSR = typeof window === 'undefined';

  if (isSSR) {
    // During SSR, use the global slug lookup function exposed by dataLoader.js
    // This directly calls the Algolia-based lookup without needing browser fetch
    if (typeof global !== 'undefined' && global.__SSR_SLUG_LOOKUP__) {
      return global.__SSR_SLUG_LOOKUP__(identifier);
    }
    // Fallback if global function not available (shouldn't happen in production)
    throw new Error('SSR slug lookup function not available');
  }

  // Client-side: use the API endpoint
  return lookupUserBySlug(identifier);
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const identifier = params.id;
  const state = getState();
  // Ensure userId is always a string for comparison
  const currentUserId =
    typeof state.ProfilePage?.userId === 'string'
      ? state.ProfilePage.userId
      : state.ProfilePage?.userId?.uuid;

  // Helper to check if we're loading data for the same user
  // If so, we can skip resetting state to avoid flicker during hydration
  const isSameUser = userId => currentUserId && currentUserId === userId;

  // If the identifier is already a UUID, load directly
  if (isUUID(identifier)) {
    // Only clear state if we're loading a different user
    // This prevents flicker when hydrating SSR-rendered pages
    if (!isSameUser(identifier)) {
      dispatch(setInitialState());
    }
    const userId = new UUID(identifier);
    return dispatch(loadProfileData(userId, params, config));
  }

  // Otherwise, it's a slug - look up the user ID first
  // This works in both SSR (via global.__SSR_SLUG_LOOKUP__) and client (via API)
  return performSlugLookup(identifier)
    .then(result => {
      if (!result.found) {
        // User not found by slug - dispatch error
        const error = new Error('User not found');
        error.status = 404;
        return dispatch(showUserError(storableError(error)));
      }

      // Only clear state if we're loading a different user
      // This prevents flicker when hydrating SSR-rendered pages
      if (!isSameUser(result.userId)) {
        dispatch(setInitialState());
      }

      const userId = new UUID(result.userId);
      return dispatch(loadProfileData(userId, params, config));
    })
    .catch(e => {
      // Slug lookup failed - try treating it as UUID anyway as fallback
      // This handles edge cases where the API might be unavailable
      console.warn('Slug lookup failed, attempting UUID fallback:', e);
      try {
        const userId = new UUID(identifier);
        return dispatch(loadProfileData(userId, params, config));
      } catch (uuidError) {
        return dispatch(showUserError(storableError(e)));
      }
    });
};
